import type { BulkEditState } from '../slice'
import type { FieldName } from '../types'

type FieldRule = {
  description: string
  triggerFields: FieldName[]
  condition: (state: BulkEditState, fieldName: FieldName) => boolean
  outcome: (state: BulkEditState, fieldName: FieldName) => void
}

/**
 * Rules can be created to force a change in state without needing to chain reducers in the slice.
 *
 * triggerFields are any fields that should trigger the rule.
 * The triggering field is passed in when the rules are applied in the slice
 *
 * Example if "engineMarket" is the only trigger, only changes to engineMarket will trigger the rule.
 *
 * Order does matter, rules that might be inacted by other rules should occur last.
 *
 * Condition is to check if the rule if a rule should be enforced.
 * Outcome modifies state to enforce the rule.
 */

export const fieldRules: FieldRule[] = [
  {
    description: 'If Engine Market is deactivated, deactivate children.',
    triggerFields: ['engineMarket', 'subIndustry', 'productId', 'emissionTier'],
    condition: (state, triggerFieldName) =>
      triggerFieldName === 'engineMarket' && !state.activeFields.engineMarket,
    outcome: function (state) {
      const [, ...childrenFields] = this.triggerFields
      childrenFields.forEach((fieldName) => {
        state.activeFields[fieldName] = false
        state.openFields[fieldName] = false
      })
    },
  },

  {
    description: 'When a trigger field is set active, so is Engine Market.',
    triggerFields: ['subIndustry', 'productId', 'emissionTier'],
    condition: function (state) {
      return this.triggerFields.some((field) => state.activeFields[field])
    },
    outcome: (state) => {
      state.activeFields.engineMarket = true
      state.openFields.engineMarket = true
    },
  },

  {
    description:
      'Engine Market being active in replace mode forces children to do the same',
    triggerFields: ['engineMarket', 'subIndustry', 'productId', 'emissionTier'],
    condition: (state) =>
      state.fieldAction.engineMarket === 'replace' &&
      state.activeFields.engineMarket,
    outcome: function (state) {
      this.triggerFields.forEach((field: FieldName) => {
        state.activeFields[field] = true
        state.fieldAction[field] = 'replace'
      })
    },
  },
]

export function applyRules(state: BulkEditState, fieldName: FieldName) {
  fieldRules
    .filter((rule) => rule.triggerFields.includes(fieldName))
    .forEach((rule) => {
      if (rule.condition(state, fieldName)) {
        rule.outcome(state, fieldName)
      }
    })
}
